import { PluginKey, TextSelection, Selection, SelectionRange, NodeSelection } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { Fragment, Slice } from 'prosemirror-model';

var deviceSystem = function() {
    var isAndroid = window.navigator.userAgent.match(/android/gi);
    var isIPhone = window.navigator.userAgent.match(/iphone/gi);
    var isIOS = /ipad|iphone/i.test(navigator.userAgent);
    return {
        isAndroid: isAndroid,
        isIPhone: isIPhone,
        isIOS: isIOS
    };
};

/* eslint-disable @typescript-eslint/no-use-before-define */ // Because working with row and column-spanning cells is not quite
// trivial, this code builds up a descriptive structure for a given
// table node. The structures are cached with the (persistent) table
// nodes as key, so that they only have to be recomputed when the
// content of the table changes.
//
// This does mean that they have to store table-relative, not
// document-relative positions. So code that uses them will typically
// compute the start position of the table and offset positions passed
// to or gotten from this structure by that amount.
function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var readFromCache;
var addToCache;
// Prefer using a weak map to cache table maps. Fall back on a
// fixed-size cache if that's not supported.
if (typeof WeakMap !== "undefined") {
    var cache = new WeakMap();
    readFromCache = function(key) {
        return cache.get(key);
    };
    addToCache = function(key, value) {
        cache.set(key, value);
        return value;
    };
} else {
    var cache1 = [];
    var cacheSize = 10;
    var cachePos = 0;
    readFromCache = function(key) {
        for(var i = 0; i < cache1.length; i += 2){
            if (cache1[i] === key) return cache1[i + 1];
        }
    };
    addToCache = function(key, value) {
        if (cachePos === cacheSize) cachePos = 0;
        cache1[cachePos++] = key;
        cache1[cachePos++] = value;
        return value;
    };
}
var Rect = function Rect(left, top, right, bottom) {
    _classCallCheck(this, Rect);
    this.left = left;
    this.top = top;
    this.right = right;
    this.bottom = bottom;
};
// ::- A table map describes the structore of a given table. To avoid
// recomputing them all the time, they are cached per table node. To
// be able to do that, positions saved in the map are relative to the
// start of the table, rather than the start of the document.
var TableMap = /*#__PURE__*/ function() {
    function TableMap(width, height, map, problems, colWidths) {
        _classCallCheck(this, TableMap);
        // :: number The width of the table
        this.width = width;
        // :: number The table's height
        this.height = height;
        // :: [number] A width * height array with the start position of
        // the cell covering that part of the table in each slot
        this.map = map;
        // An optional array of problems (cell overlap or non-rectangular
        // shape) for the table, used by the table normalizer.
        this.problems = problems;
        // 表格每列的宽度
        this.colWidths = colWidths;
    }
    var _proto = TableMap.prototype;
    // :: (number) → Rect
    // Find the dimensions of the cell at the given position.
    _proto.findCell = function findCell(pos) {
        for(var i = 0; i < this.map.length; i++){
            var curPos = this.map[i];
            if (curPos !== pos) continue;
            var left = i % this.width;
            var top = i / this.width | 0;
            var right = left + 1;
            var bottom = top + 1;
            for(var j = 1; right < this.width && this.map[i + j] === curPos; j++){
                right++;
            }
            for(var j1 = 1; bottom < this.height && this.map[i + this.width * j1] === curPos; j1++){
                bottom++;
            }
            return new Rect(left, top, right, bottom);
        }
        throw new RangeError("No cell with offset ".concat(pos, " found"));
    };
    // :: (number) → number
    // Find the left side of the cell at the given position.
    _proto.colCount = function colCount(pos) {
        for(var i = 0; i < this.map.length; i++){
            if (this.map[i] === pos) return i % this.width;
        }
        throw new RangeError("No cell with offset ".concat(pos, " found"));
    };
    _proto.rowCount = function rowCount(pos) {
        for(var i = 0; i < this.map.length; i++){
            if (this.map[i] === pos) return Math.floor(i / this.width);
        }
        throw new RangeError("No cell with offset ".concat(pos, " found"));
    };
    // :: (number, string, number) → ?number
    // Find the next cell in the given direction, starting from the cell
    // at `pos`, if any.
    _proto.nextCell = function nextCell(pos, axis, dir) {
        var _this_findCell = this.findCell(pos), left = _this_findCell.left, right = _this_findCell.right, top = _this_findCell.top, bottom = _this_findCell.bottom;
        if (axis === "horiz") {
            if (dir < 0 ? left === 0 : right === this.width) return null;
            return this.map[top * this.width + (dir < 0 ? left - 1 : right)];
        }
        if (dir < 0 ? top === 0 : bottom === this.height) return null;
        return this.map[left + this.width * (dir < 0 ? top - 1 : bottom)];
    };
    // :: (number, number) → Rect
    // Get the rectangle spanning the two given cells.
    _proto.rectBetween = function rectBetween(posA, posB) {
        var _this_findCell = this.findCell(posA), leftA = _this_findCell.left, rightA = _this_findCell.right, topA = _this_findCell.top, bottomA = _this_findCell.bottom;
        var _this_findCell1 = this.findCell(posB), leftB = _this_findCell1.left, rightB = _this_findCell1.right, topB = _this_findCell1.top, bottomB = _this_findCell1.bottom;
        var l = Math.min(leftA, leftB);
        var t = Math.min(topA, topB);
        var r = Math.max(rightA, rightB);
        var b = Math.max(bottomA, bottomB);
        var rect = new Rect(l, t, r, b);
        // 不断遍历更新边界，直到 Rect 是一个规则的矩形
        while(true){
            for(var i = l; i < r; i++){
                var top = this.findCell(this.map[this.width * t + i]).top;
                t = Math.min(top, t);
                var bottom = this.findCell(this.map[this.width * (b - 1) + i]).bottom;
                b = Math.max(bottom, b);
            }
            for(var i1 = t; i1 < b; i1++){
                var left = this.findCell(this.map[this.width * i1 + l]).left;
                l = Math.min(left, l);
                var right = this.findCell(this.map[this.width * i1 + r - 1]).right;
                r = Math.max(right, r);
            }
            if (l === rect.left && t === rect.top && r === rect.right && b === rect.bottom) {
                break;
            }
            rect = new Rect(l, t, r, b);
        }
        return rect;
    };
    // :: (Rect) → [number]
    // Return the position of all cells that have the top left corner in
    // the given rectangle.
    _proto.cellsInRect = function cellsInRect(rect) {
        var result = [];
        var seen = {};
        for(var row = rect.top; row < rect.bottom; row++){
            for(var col = rect.left; col < rect.right; col++){
                var index = row * this.width + col;
                var pos = this.map[index];
                if (seen[pos]) continue;
                seen[pos] = true;
                if ((col !== rect.left || !col || this.map[index - 1] !== pos) && (row !== rect.top || !row || this.map[index - this.width] !== pos)) {
                    result.push(pos);
                }
            }
        }
        return result;
    };
    // :: (number, number, Node) → number
    // Return the position at which the cell at the given row and column
    // starts, or would start, if a cell started there.
    _proto.positionAt = function positionAt(row, col, table) {
        for(var i = 0, rowStart = 0;; i++){
            var rowEnd = rowStart + table.child(i).nodeSize;
            if (i === row) {
                var index = col + row * this.width;
                var rowEndIndex = (row + 1) * this.width;
                // Skip past cells from previous rows (via rowspan)
                while(index < rowEndIndex && this.map[index] < rowStart)index++;
                return index === rowEndIndex ? rowEnd - 1 : this.map[index];
            }
            rowStart = rowEnd;
        }
    };
    // :: (Node) → TableMap
    // Find the table map for the given table node.
    TableMap.get = function get(table) {
        return readFromCache(table) || addToCache(table, computeMap(table));
    };
    return TableMap;
}();
/**
 * 是否需要修正th
 */ function needFixRowHeader(rowNode, table) {
    var rowNodeChildArr = rowNode.content.content;
    // 如果是空，或者首行只有一个元素，不修
    if (rowNodeChildArr.length <= 1) return false;
    var table_header = table.type.schema.nodes.table_header;
    var tableHeaderTotalCount = rowNodeChildArr.reduce(function(acc, node) {
        return node.type === table_header ? acc + 1 : acc;
    }, 0);
    // 首行有th，且数量小于首行元素总数，大于1（即至少有两个，表头列可能会有一个）
    return tableHeaderTotalCount > 1 && tableHeaderTotalCount < rowNodeChildArr.length;
}
// Compute a table map.
function computeMap(table) {
    if (table.type.spec.tableRole !== "table") {
        throw new RangeError("Not a table node: ".concat(table.type.name));
    }
    var width = findWidth(table);
    var height = table.childCount;
    var map = [];
    var mapPos = 0;
    var problems = null;
    var colWidths = [];
    for(var i = 0, e = width * height; i < e; i++)map[i] = 0;
    for(var row = 0, pos = 0; row < height; row++){
        var rowNode = table.child(row);
        if (row === 0) {
            /**
       * fix: #66078869, #67565651 表头行单元格非法，应该是th，实际是td
       * @see https://km.sankuai.com/collabpage/1347228195
       */ if (needFixRowHeader(rowNode, table)) {
                (problems || (problems = [])).push({
                    type: "invalid_cell_for_row_header_table"
                });
            }
        }
        pos++;
        for(var i1 = 0;; i1++){
            while(mapPos < map.length && map[mapPos] !== 0)mapPos++;
            if (i1 === rowNode.childCount) break;
            var cellNode = rowNode.child(i1);
            var _cellNode_attrs = cellNode.attrs, colspan = _cellNode_attrs.colspan, rowspan = _cellNode_attrs.rowspan, colwidth = _cellNode_attrs.colwidth;
            for(var h = 0; h < rowspan; h++){
                if (h + row >= height) {
                    (problems || (problems = [])).push({
                        type: "overlong_rowspan",
                        pos: pos,
                        n: rowspan - h
                    });
                    break;
                }
                var start = mapPos + h * width;
                for(var w = 0; w < colspan; w++){
                    if (map[start + w] === 0) map[start + w] = pos;
                    else {
                        (problems || (problems = [])).push({
                            type: "collision",
                            row: row,
                            pos: pos,
                            n: colspan - w
                        });
                    }
                    var colW = colwidth && colwidth[w];
                    if (colW) {
                        var widthIndex = (start + w) % width * 2;
                        var prev = colWidths[widthIndex];
                        if (prev == null || prev !== colW && colWidths[widthIndex + 1] === 1) {
                            colWidths[widthIndex] = colW;
                            colWidths[widthIndex + 1] = 1;
                        } else if (prev === colW) {
                            colWidths[widthIndex + 1]++;
                        }
                    }
                }
            }
            mapPos += colspan;
            pos += cellNode.nodeSize;
        }
        var expectedPos = (row + 1) * width;
        var missing = 0;
        while(mapPos < expectedPos)if (map[mapPos++] === 0) missing++;
        if (missing) {
            (problems || (problems = [])).push({
                type: "missing",
                row: row,
                n: missing
            });
        }
        pos++;
    }
    var tableMap = new TableMap(width, height, map, problems, colWidths.filter(function(x, i) {
        return i % 2 === 0;
    }));
    var badWidths = false;
    // For columns that have defined widths, but whose widths disagree
    // between rows, fix up the cells whose width doesn't match the
    // computed one.
    for(var i2 = 0; !badWidths && i2 < colWidths.length; i2 += 2){
        if (colWidths[i2] != null && colWidths[i2 + 1] < height) badWidths = true;
    }
    if (badWidths) findBadColWidths(tableMap, colWidths, table);
    return tableMap;
}
function findWidth(table) {
    var width = -1;
    var hasRowSpan = false;
    for(var row = 0; row < table.childCount; row++){
        var rowNode = table.child(row);
        var rowWidth = 0;
        if (hasRowSpan) {
            for(var j = 0; j < row; j++){
                var prevRow = table.child(j);
                for(var i = 0; i < prevRow.childCount; i++){
                    var cell = prevRow.child(i);
                    if (j + cell.attrs.rowspan > row) rowWidth += cell.attrs.colspan;
                }
            }
        }
        for(var i1 = 0; i1 < rowNode.childCount; i1++){
            var cell1 = rowNode.child(i1);
            rowWidth += cell1.attrs.colspan;
            if (cell1.attrs.rowspan > 1) hasRowSpan = true;
        }
        if (width === -1) width = rowWidth;
        else if (width !== rowWidth) width = Math.max(width, rowWidth);
    }
    return width;
}
function findBadColWidths(map, colWidths, table) {
    if (!map.problems) map.problems = [];
    for(var i = 0, seen = {}; i < map.map.length; i++){
        var pos = map.map[i];
        if (seen[pos]) continue;
        seen[pos] = true;
        var node = table.nodeAt(pos);
        var updated = null;
        for(var j = 0; j < node.attrs.colspan; j++){
            var col = (i + j) % map.width;
            var colWidth = colWidths[col * 2];
            if (colWidth != null && (!node.attrs.colwidth || node.attrs.colwidth[j] !== colWidth)) {
                (updated || (updated = freshColWidth(node.attrs)))[j] = colWidth;
            }
        }
        if (updated) {
            map.problems.unshift({
                type: "colwidth mismatch",
                pos: pos,
                colwidth: updated
            });
        }
    }
}
function freshColWidth(attrs) {
    if (attrs.colwidth) return attrs.colwidth.slice();
    var result = [];
    for(var i = 0; i < attrs.colspan; i++)result.push(0);
    return result;
}

// Various helper function for working with tables
var key = new PluginKey("selectingCells");
function cellAround($pos) {
    for(var d = $pos.depth - 1; d > 0; d--){
        if ($pos.node(d).type.spec.tableRole === "row") {
            return $pos.node(0).resolve($pos.before(d + 1));
        }
    }
    return null;
}
function cellWrapping($pos) {
    for(var d = $pos.depth; d > 0; d--){
        // Sometimes the cell can be in the same depth.
        var role = $pos.node(d).type.spec.tableRole;
        if (role === "cell" || role === "header_cell") return $pos.node(d);
    }
    return null;
}
function isInTable(state) {
    var $head = state.selection.$head;
    for(var d = $head.depth; d > 0; d--){
        if ($head.node(d).type.spec.tableRole === "row") return true;
    }
    return false;
}
function cellNear($pos) {
    for(var after = $pos.nodeAfter, pos = $pos.pos; after; after = after.firstChild, pos++){
        var role = after.type.spec.tableRole;
        if (role === "cell" || role === "header_cell") return $pos.doc.resolve(pos);
    }
    for(var before = $pos.nodeBefore, pos1 = $pos.pos; before; before = before.lastChild, pos1--){
        var role1 = before.type.spec.tableRole;
        if (role1 === "cell" || role1 === "header_cell") {
            return $pos.doc.resolve(pos1 - before.nodeSize);
        }
    }
}
function selectionCell(state) {
    var sel = state.selection;
    if (sel.$anchorCell) {
        return sel.$anchorCell.pos > sel.$headCell.pos ? sel.$anchorCell : sel.$headCell;
    }
    if (sel.node && sel.node.type.spec.tableRole === "cell") {
        return sel.$anchor;
    }
    return cellAround(sel.$head) || cellNear(sel.$head);
}
function pointsAtCell($pos) {
    return $pos.parent.type.spec.tableRole === "row" && $pos.nodeAfter;
}
function moveCellForward($pos) {
    return $pos.node(0).resolve($pos.pos + $pos.nodeAfter.nodeSize);
}
function inSameTable($a, $b) {
    return $a.depth === $b.depth && $a.pos >= $b.start(-1) && $a.pos <= $b.end(-1);
}
function findCell($pos) {
    return TableMap.get($pos.node(-1)).findCell($pos.pos - $pos.start(-1));
}
function colCount($pos) {
    return TableMap.get($pos.node(-1)).colCount($pos.pos - $pos.start(-1));
}
function nextCell($pos, axis, dir) {
    var start = $pos.start(-1);
    var map = TableMap.get($pos.node(-1));
    var moved = map.nextCell($pos.pos - start, axis, dir);
    return moved == null ? null : $pos.node(0).resolve(start + moved);
}
function setAttr(attrs, name, value) {
    var result = {};
    for(var prop in attrs)result[prop] = attrs[prop];
    result[name] = value;
    return result;
}
function removeColSpan(attrs, pos) {
    var n = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 1;
    var result = setAttr(attrs, "colspan", attrs.colspan - n);
    if (result.colwidth) {
        result.colwidth = result.colwidth.slice();
        result.colwidth.splice(pos, n);
        if (!result.colwidth.some(function(w) {
            return w > 0;
        })) result.colwidth = null;
    }
    return result;
}
function addColSpan(attrs, pos, widths) {
    // widths 表示新增列的宽度，widths.length 表示新增几列
    var n = widths.length;
    var result = setAttr(attrs, "colspan", attrs.colspan + n);
    if (result.colwidth) {
        result.colwidth = result.colwidth.slice();
        for(var i = 0; i < n; i++)result.colwidth.splice(pos, 0, widths[i]);
    }
    return result;
}

/* eslint-disable @typescript-eslint/no-use-before-define */ // This file defines a ProseMirror selection subclass that models
// table cell selections. The table plugin needs to be active to wire
// in the user interaction part of table selections (so that you
// actually get such selections when you select across cells).
function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Date.prototype.toString.call(Reflect.construct(Date, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _construct(Parent, args, Class) {
    if (isNativeReflectConstruct()) {
        _construct = Reflect.construct;
    } else {
        _construct = function _construct(Parent, args, Class) {
            var a = [
                null
            ];
            a.push.apply(a, args);
            var Constructor = Function.bind.apply(Parent, a);
            var instance = new Constructor();
            if (Class) _setPrototypeOf(instance, Class.prototype);
            return instance;
        };
    }
    return _construct.apply(null, arguments);
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _isNativeFunction(fn) {
    return Function.toString.call(fn).indexOf("[native code]") !== -1;
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _wrapNativeSuper(Class) {
    var _cache = typeof Map === "function" ? new Map() : undefined;
    _wrapNativeSuper = function _wrapNativeSuper(Class) {
        if (Class === null || !_isNativeFunction(Class)) return Class;
        if (typeof Class !== "function") {
            throw new TypeError("Super expression must either be null or a function");
        }
        if (typeof _cache !== "undefined") {
            if (_cache.has(Class)) return _cache.get(Class);
            _cache.set(Class, Wrapper);
        }
        function Wrapper() {
            return _construct(Class, arguments, _getPrototypeOf(this).constructor);
        }
        Wrapper.prototype = Object.create(Class.prototype, {
            constructor: {
                value: Wrapper,
                enumerable: false,
                writable: true,
                configurable: true
            }
        });
        return _setPrototypeOf(Wrapper, Class);
    };
    return _wrapNativeSuper(Class);
}
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
// ::- A [`Selection`](http://prosemirror.net/docs/ref/#state.Selection)
// subclass that represents a cell selection spanning part of a table.
// With the plugin enabled, these will be created when the user
// selects across cells, and will be drawn by giving selected cells a
// `selectedCell` CSS class.
var CellSelection = /*#__PURE__*/ function(Selection1) {
    _inherits(CellSelection, Selection1);
    var _super = _createSuper(CellSelection);
    function CellSelection($anchorCell) {
        var $headCell = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : $anchorCell;
        _classCallCheck$1(this, CellSelection);
        var _this;
        var table = $anchorCell.node(-1);
        var map = TableMap.get(table);
        var start = $anchorCell.start(-1);
        var rect = map.rectBetween($anchorCell.pos - start, $headCell.pos - start);
        var doc = $anchorCell.node(0);
        var cells = map.cellsInRect(rect).filter(function(p) {
            return p !== $headCell.pos - start;
        });
        // Make the head cell the first range, so that it counts as the
        // primary part of the selection
        cells.unshift($headCell.pos - start);
        var ranges = cells.map(function(pos) {
            var cell = table.nodeAt(pos);
            var from = pos + start + 1;
            return new SelectionRange(doc.resolve(from), doc.resolve(from + cell.content.size));
        });
        var $selectionTo = ranges[0].$to;
        if (deviceSystem().isIOS) {
            $selectionTo = ranges[0].$from;
        }
        _this = _super.call(this, ranges[0].$from, $selectionTo, ranges);
        // :: ResolvedPos
        // A resolved position pointing _in front of_ the anchor cell (the one
        // that doesn't move when extending the selection).
        _this.$anchorCell = $anchorCell;
        // :: ResolvedPos
        // A resolved position pointing in front of the head cell (the one
        // moves when extending the selection).
        _this.$headCell = $headCell;
        return _this;
    }
    var _proto = CellSelection.prototype;
    _proto.map = function map(doc, mapping) {
        var $anchorCell = doc.resolve(mapping.map(this.$anchorCell.pos));
        var $headCell = doc.resolve(mapping.map(this.$headCell.pos));
        if (pointsAtCell($anchorCell) && pointsAtCell($headCell) && inSameTable($anchorCell, $headCell)) {
            var tableChanged = this.$anchorCell.node(-1) !== $anchorCell.node(-1);
            if (tableChanged && this.isRowSelection()) {
                return CellSelection.rowSelection($anchorCell, $headCell);
            }
            if (tableChanged && this.isColSelection()) {
                return CellSelection.colSelection($anchorCell, $headCell);
            }
            return new CellSelection($anchorCell, $headCell);
        }
        return TextSelection.between($anchorCell, $headCell);
    };
    // :: () → Slice
    // Returns a rectangular slice of table rows containing the selected
    // cells.
    _proto.content = function content() {
        var table = this.$anchorCell.node(-1);
        var map = TableMap.get(table);
        var start = this.$anchorCell.start(-1);
        var rect = map.rectBetween(this.$anchorCell.pos - start, this.$headCell.pos - start);
        var seen = {};
        var rows = [];
        for(var row = rect.top; row < rect.bottom; row++){
            var rowContent = [];
            for(var index = row * map.width + rect.left, col = rect.left; col < rect.right; col++, index++){
                var pos = map.map[index];
                if (!seen[pos]) {
                    seen[pos] = true;
                    var cellRect = map.findCell(pos);
                    var cell = table.nodeAt(pos);
                    var extraLeft = rect.left - cellRect.left;
                    var extraRight = cellRect.right - rect.right;
                    if (extraLeft > 0 || extraRight > 0) {
                        var attrs = cell.attrs;
                        if (extraLeft > 0) attrs = removeColSpan(attrs, 0, extraLeft);
                        if (extraRight > 0) {
                            attrs = removeColSpan(attrs, attrs.colspan - extraRight, extraRight);
                        }
                        if (cellRect.left < rect.left) {
                            cell = cell.type.createAndFill(attrs);
                        } else cell = cell.type.create(attrs, cell.content);
                    }
                    if (cellRect.top < rect.top || cellRect.bottom > rect.bottom) {
                        var attrs1 = setAttr(cell.attrs, "rowspan", Math.min(cellRect.bottom, rect.bottom) - Math.max(cellRect.top, rect.top));
                        if (cellRect.top < rect.top) cell = cell.type.createAndFill(attrs1);
                        else cell = cell.type.create(attrs1, cell.content);
                    }
                    rowContent.push(cell);
                }
            }
            rows.push(table.child(row).copy(Fragment.from(rowContent)));
        }
        var fragment = this.isColSelection() && this.isRowSelection() ? table : rows;
        return new Slice(Fragment.from(fragment), 1, 1);
    };
    _proto.replace = function replace(tr) {
        var content = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : Slice.empty;
        var mapFrom = tr.steps.length;
        var ranges = this.ranges;
        for(var i = 0; i < ranges.length; i++){
            var _ranges_i = ranges[i], $from = _ranges_i.$from, $to = _ranges_i.$to;
            var mapping = tr.mapping.slice(mapFrom);
            tr.replace(mapping.map($from.pos), mapping.map($to.pos), i ? Slice.empty : content);
        }
        var sel = Selection.findFrom(tr.doc.resolve(tr.mapping.slice(mapFrom).map(this.to)), -1);
        if (sel) tr.setSelection(sel);
    };
    _proto.replaceWith = function replaceWith(tr, node) {
        this.replace(tr, new Slice(Fragment.from(node), 0, 0));
    };
    _proto.forEachCell = function forEachCell(f) {
        var table = this.$anchorCell.node(-1);
        var map = TableMap.get(table);
        var start = this.$anchorCell.start(-1);
        var cells = map.cellsInRect(map.rectBetween(this.$anchorCell.pos - start, this.$headCell.pos - start));
        for(var i = 0; i < cells.length; i++){
            f(table.nodeAt(cells[i]), start + cells[i]);
        }
    };
    // :: () → bool
    // True if this selection goes all the way from the top to the
    // bottom of the table.
    _proto.isColSelection = function isColSelection() {
        var anchorTop = this.$anchorCell.index(-1);
        var headTop = this.$headCell.index(-1);
        if (Math.min(anchorTop, headTop) > 0) return false;
        var anchorBot = anchorTop + this.$anchorCell.nodeAfter.attrs.rowspan;
        var headBot = headTop + this.$headCell.nodeAfter.attrs.rowspan;
        return Math.max(anchorBot, headBot) === this.$headCell.node(-1).childCount;
    };
    // 是否选中整个表格
    _proto.isWholeSelection = function isWholeSelection() {
        var map = TableMap.get(this.$anchorCell.node(-1));
        var rect = this.getRect();
        if (rect.top === 0 && rect.bottom === map.height && rect.left === 0 && rect.right === map.width) {
            return true;
        }
        return false;
    };
    // :: () → bool
    // True if this selection goes all the way from the left to the
    // right of the table.
    _proto.isRowSelection = function isRowSelection() {
        var map = TableMap.get(this.$anchorCell.node(-1));
        var start = this.$anchorCell.start(-1);
        var anchorLeft = map.colCount(this.$anchorCell.pos - start);
        var headLeft = map.colCount(this.$headCell.pos - start);
        if (Math.min(anchorLeft, headLeft) > 0) return false;
        var anchorRight = anchorLeft + this.$anchorCell.nodeAfter.attrs.colspan;
        var headRight = headLeft + this.$headCell.nodeAfter.attrs.colspan;
        return Math.max(anchorRight, headRight) === map.width;
    };
    _proto.eq = function eq(other) {
        return _instanceof(other, CellSelection) && other.$anchorCell.pos === this.$anchorCell.pos && other.$headCell.pos === this.$headCell.pos;
    };
    // 获取CellSelection的上下左右范围
    _proto.getRect = function getRect() {
        var table = this.$anchorCell.node(-1);
        var map = TableMap.get(table);
        var start = this.$anchorCell.start(-1);
        var rect = map.rectBetween(this.$anchorCell.pos - start, this.$headCell.pos - start);
        return rect;
    };
    _proto.toJSON = function toJSON() {
        return {
            type: "cell",
            anchor: this.$anchorCell.pos,
            head: this.$headCell.pos
        };
    };
    _proto.getBookmark = function getBookmark() {
        return new CellBookmark(this.$anchorCell.pos, this.$headCell.pos);
    };
    // :: (ResolvedPos, ?ResolvedPos) → CellSelection
    // Returns the smallest column selection that covers the given anchor
    // and head cell.
    CellSelection.colSelection = function colSelection($anchorCell) {
        var $headCell = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : $anchorCell;
        var map = TableMap.get($anchorCell.node(-1));
        var start = $anchorCell.start(-1);
        var anchorRect = map.findCell($anchorCell.pos - start);
        var headRect = map.findCell($headCell.pos - start);
        var doc = $anchorCell.node(0);
        if (anchorRect.top <= headRect.top) {
            if (anchorRect.top > 0) {
                $anchorCell = doc.resolve(start + map.map[anchorRect.left]);
            }
            if (headRect.bottom < map.height) {
                $headCell = doc.resolve(start + map.map[map.width * (map.height - 1) + headRect.right - 1]);
            }
        } else {
            if (headRect.top > 0) {
                $headCell = doc.resolve(start + map.map[headRect.left]);
            }
            if (anchorRect.bottom < map.height) {
                $anchorCell = doc.resolve(start + map.map[map.width * (map.height - 1) + anchorRect.right - 1]);
            }
        }
        return new CellSelection($anchorCell, $headCell);
    };
    // :: (ResolvedPos, ?ResolvedPos) → CellSelection
    // Returns the smallest row selection that covers the given anchor
    // and head cell.
    CellSelection.rowSelection = function rowSelection($anchorCell) {
        var $headCell = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : $anchorCell;
        var map = TableMap.get($anchorCell.node(-1));
        var start = $anchorCell.start(-1);
        var anchorRect = map.findCell($anchorCell.pos - start);
        var headRect = map.findCell($headCell.pos - start);
        var doc = $anchorCell.node(0);
        if (anchorRect.left <= headRect.left) {
            if (anchorRect.left > 0) {
                $anchorCell = doc.resolve(start + map.map[anchorRect.top * map.width]);
            }
            if (headRect.right < map.width) {
                $headCell = doc.resolve(start + map.map[map.width * (headRect.top + 1) - 1]);
            }
        } else {
            if (headRect.left > 0) {
                $headCell = doc.resolve(start + map.map[headRect.top * map.width]);
            }
            if (anchorRect.right < map.width) {
                $anchorCell = doc.resolve(start + map.map[map.width * (anchorRect.top + 1) - 1]);
            }
        }
        return new CellSelection($anchorCell, $headCell);
    };
    CellSelection.fromJSON = function fromJSON(doc, json) {
        return new CellSelection(doc.resolve(json.anchor), doc.resolve(json.head));
    };
    // :: (Node, number, ?number) → CellSelection
    CellSelection.create = function create(doc, anchorCell) {
        var headCell = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : anchorCell;
        return new CellSelection(doc.resolve(anchorCell), doc.resolve(headCell));
    };
    return CellSelection;
}(_wrapNativeSuper(Selection));
CellSelection.prototype.visible = false;
(function() {
    var id = "parker_cell";
    if (window["__".concat(id, "__")]) {
        return;
    }
    window["__".concat(id, "__")] = true;
    Selection.jsonID(id, CellSelection);
})();
var CellBookmark = /*#__PURE__*/ function() {
    function CellBookmark(anchor, head) {
        _classCallCheck$1(this, CellBookmark);
        this.anchor = anchor;
        this.head = head;
    }
    var _proto = CellBookmark.prototype;
    _proto.map = function map(mapping) {
        return new CellBookmark(mapping.map(this.anchor), mapping.map(this.head));
    };
    _proto.resolve = function resolve(doc) {
        var $anchorCell = doc.resolve(this.anchor);
        var $headCell = doc.resolve(this.head);
        if ($anchorCell.parent.type.spec.tableRole === "row" && $headCell.parent.type.spec.tableRole === "row" && $anchorCell.index() < $anchorCell.parent.childCount && $headCell.index() < $headCell.parent.childCount && inSameTable($anchorCell, $headCell)) {
            return new CellSelection($anchorCell, $headCell);
        }
        return Selection.near($headCell, 1);
    };
    return CellBookmark;
}();
function drawCellSelection(state) {
    if (!_instanceof(state.selection, CellSelection)) return null;
    var cells = [];
    var sel = state.selection;
    var $pos = selectionCell(state);
    var table = $pos.node(-1);
    var tableStart = $pos.start(-1);
    var map = TableMap.get(table);
    var totalRect = map.rectBetween(sel.$anchorCell.pos - tableStart, sel.$headCell.pos - tableStart);
    state.selection.forEachCell(function(node, pos) {
        var className = "selectedCell";
        var rect = map.findCell(pos - tableStart);
        if (rect.left === totalRect.left) className += " left";
        if (rect.right === totalRect.right) className += " right";
        if (rect.top === totalRect.top) className += " top";
        if (rect.bottom === totalRect.bottom) className += " bottom";
        cells.push(Decoration.node(pos, pos + node.nodeSize, {
            class: className
        }));
    });
    return DecorationSet.create(state.doc, cells);
}
function isCellBoundarySelection(param) {
    var $from = param.$from, $to = param.$to;
    if ($from.pos === $to.pos || $from.pos < $from.pos - 6) return false; // Cheap elimination
    var afterFrom = $from.pos;
    var beforeTo = $to.pos;
    var depth = $from.depth;
    for(; depth >= 0; depth--, afterFrom++){
        if ($from.after(depth + 1) < $from.end(depth)) break;
    }
    for(var d = $to.depth; d >= 0; d--, beforeTo--){
        if ($to.before(d + 1) > $to.start(d)) break;
    }
    return afterFrom === beforeTo && /row|table/.test($from.node(depth).type.spec.tableRole);
}
function normalizeSelection(state, tr, oldState, allowTableNodeSelection) {
    var sel = (tr || state).selection;
    var oldSel = oldState.selection;
    var doc = (tr || state).doc;
    var normalize;
    var role;
    if (_instanceof(sel, NodeSelection) && sel.node.type.spec.tableRole) {
        role = sel.node.type.spec.tableRole;
        if (role === "cell" || role === "header_cell") {
            normalize = CellSelection.create(doc, sel.from);
        } else if (role === "row") {
            var $cell = doc.resolve(sel.from + 1);
            normalize = CellSelection.rowSelection($cell, $cell);
        } else if (!allowTableNodeSelection) {
            var map = TableMap.get(sel.node);
            var start = sel.from + 1;
            var lastCell = start + map.map[map.width * map.height - 1];
            normalize = CellSelection.create(doc, start + 1, lastCell);
        }
    } else if (_instanceof(sel, TextSelection) && isCellBoundarySelection(sel)) {
        normalize = TextSelection.create(doc, sel.from);
    } else if (_instanceof(sel, TextSelection) && _instanceof(oldSel, CellSelection) && sel.from >= oldSel.from && sel.to <= oldSel.to && sel.from !== sel.to) {
        normalize = oldSel;
    }
    if (normalize) (tr || (tr = state.tr)).setSelection(normalize);
    return tr;
}

export { CellSelection, TableMap, addColSpan, cellAround, cellWrapping, colCount, drawCellSelection, findCell, inSameTable, isInTable, moveCellForward, nextCell, normalizeSelection, pointsAtCell, removeColSpan, selectionCell, setAttr };
//# sourceMappingURL=index.js.map
